import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../redux/store';
import { setAuth, setUser } from '../../redux/authSlice';
import { getTheme } from '../../theme/themes';
import axios from '../../interceptors/axios';

const ADMIN_ITEMS: { key: string; icon: string; path: string }[] = [];

const NEW_PLATFORM_ITEMS = [
  { key: 'nav.dashboard',  icon: '⊞', path: '/dashboard' },
  { key: 'nav.reports',    icon: '📊', path: '/reports' },
  { key: 'nav.media',      icon: '🎬', path: '/media' },
  { key: 'nav.playlists',  icon: '▤',  path: '/playlists' },
  { key: 'nav.schedules',  icon: '📅', path: '/schedules' },
  { key: 'nav.screens',    icon: '🖥', path: '/screens' },
];

interface SidebarProps {
  onClose?: () => void;
  themeId: string;
  onThemeChange: (id: string) => void;
}

const APK_URL = 'https://app.promovideo.net.ua/uploads/apk/promovideo.apk';
const VERSION_URL = 'https://app.promovideo.net.ua/uploads/apk/version.json';

interface StorageStats { used_bytes: number; file_count: number; limit_bytes: number; }

const PvLogo: React.FC<{ size: number }> = ({ size }) => (
  <Box sx={{ width: size, height: size, flexShrink: 0, overflow: 'hidden', borderRadius: `${Math.round(size * 0.215)}px`, boxShadow: '0 4px 14px rgba(45,10,107,0.55)' }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width={size} height={size} style={{ display: 'block' }}>
      <defs>
        <linearGradient id="pvBg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1A0533"/>
          <stop offset="50%" stopColor="#2D0A6B"/>
          <stop offset="100%" stopColor="#0D1B6B"/>
        </linearGradient>
        <linearGradient id="pvPlay" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF3CAC"/>
          <stop offset="50%" stopColor="#FF6B3D"/>
          <stop offset="100%" stopColor="#FFD700"/>
        </linearGradient>
        <linearGradient id="pvFilm" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FF3CAC" stopOpacity="0.15"/>
          <stop offset="50%" stopColor="#FF6B3D" stopOpacity="0.3"/>
          <stop offset="100%" stopColor="#FFD700" stopOpacity="0.15"/>
        </linearGradient>
        <linearGradient id="pvSpark" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFD700"/>
          <stop offset="100%" stopColor="#FF6B3D"/>
        </linearGradient>
        <filter id="pvGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="10" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="pvSoftGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="6" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <clipPath id="pvClip">
          <rect x="0" y="0" width="512" height="512" rx="110" ry="110"/>
        </clipPath>
      </defs>
      <g clipPath="url(#pvClip)">
        <rect width="512" height="512" fill="url(#pvBg)"/>
        {/* film strip top */}
        <rect x="0" y="30" width="512" height="58" fill="url(#pvFilm)" rx="4"/>
        {[18,66,114,162,210,258,306,354,402,450].map(x => (
          <rect key={x} x={x} y="40" width="28" height="38" fill="none" stroke="white" strokeOpacity="0.12" strokeWidth="1.5" rx="4"/>
        ))}
        {/* film strip bottom */}
        <rect x="0" y="424" width="512" height="58" fill="url(#pvFilm)" rx="4"/>
        {[18,66,114,162,210,258,306,354,402,450].map(x => (
          <rect key={x} x={x} y="434" width="28" height="38" fill="none" stroke="white" strokeOpacity="0.12" strokeWidth="1.5" rx="4"/>
        ))}
        <circle cx="256" cy="256" r="148" fill="none" stroke="url(#pvPlay)" strokeWidth="2.5" strokeOpacity="0.35" filter="url(#pvSoftGlow)"/>
        <circle cx="256" cy="256" r="128" fill="white" fillOpacity="0.06"/>
        <circle cx="256" cy="256" r="128" fill="none" stroke="url(#pvPlay)" strokeWidth="3"/>
        <polygon points="220,199 220,313 330,256" fill="url(#pvPlay)" filter="url(#pvGlow)"/>
        <polygon points="220,199 220,234 290,231" fill="white" fillOpacity="0.15"/>
        <g transform="translate(370,154)"><polygon points="0,-14 3,-3 14,0 3,3 0,14 -3,3 -14,0 -3,-3" fill="url(#pvSpark)" opacity="0.9"/></g>
        <g transform="translate(148,172)"><polygon points="0,-9 2,-2 9,0 2,2 0,9 -2,2 -9,0 -2,-2" fill="#FF3CAC" opacity="0.7"/></g>
        <g transform="translate(390,342)"><polygon points="0,-7 1.5,-1.5 7,0 1.5,1.5 0,7 -1.5,1.5 -7,0 -1.5,-1.5" fill="#FFD700" opacity="0.6"/></g>
      </g>
    </svg>
  </Box>
);

const Sidebar: React.FC<SidebarProps> = ({ onClose, themeId, onThemeChange }) => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((s: RootState) => s.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    !onClose && localStorage.getItem('pv-sidebar') === 'collapsed'
  );
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const [apkVersion, setApkVersion] = useState<string | null>(null);
  const [storageStats, setStorageStats] = useState<StorageStats | null>(null);

  useEffect(() => {
    fetch(VERSION_URL)
      .then(r => r.json())
      .then(d => setApkVersion(d.version_name))
      .catch(() => {});
  }, []);

  const fetchStorage = useCallback(async () => {
    try {
      const { data } = await axios.get<StorageStats>('/media/storage-stats');
      setStorageStats(data);
    } catch {}
  }, []);

  useEffect(() => { fetchStorage(); }, [fetchStorage]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('pv-sidebar', next ? 'collapsed' : 'open');
  };

  const logout = () => {
    localStorage.removeItem('token');
    dispatch(setAuth(false));
    dispatch(setUser(null));
    navigate('/login');
  };

  const switchLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('pv-lang', lang);
  };

  const isDark = getTheme(themeId).mode === 'dark';
  const toggleTheme = () => onThemeChange(isDark ? 'sky' : 'navy');

  const w = collapsed ? '56px' : '248px';

  // One icon size in both states, in a fixed box — otherwise the glyphs resize
  // and shift on every collapse/expand
  const navIconSx = { width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, lineHeight: 1, flexShrink: 0 } as const;

  // Same padding and alignment collapsed or not, so the icon column stays at the
  // very same x — collapsing only narrows the sidebar and fades the labels away.
  // (8px rail padding + 2px active border + 8px row padding puts the 22px icon at
  // 18–40px, i.e. centred in the 56px rail.)
  const railPx = '8px';
  const navRowSx = {
    display: 'flex', alignItems: 'center', gap: '11px',
    px: railPx, py: '9px', minHeight: 40, borderRadius: '8px',
    justifyContent: 'flex-start',
  } as const;
  // Labels stay mounted and just fade, otherwise they pop in only after the
  // width animation has already finished
  const navLabelSx = {
    minWidth: 0, opacity: collapsed ? 0 : 1, pointerEvents: 'none' as const,
    transition: 'opacity .15s ease',
  } as const;

  const NavItem = ({ icon, label, active, path, onClick }: { icon: string; label: string; active: boolean; path?: string; onClick?: () => void }) => (
    <Tooltip title={collapsed ? label : ''} placement="right">
      <Box
        onClick={() => { if (path) { navigate(path); onClose?.(); } onClick?.(); }}
        sx={{
          ...navRowSx,
          mb: '2px',
          cursor: 'pointer', userSelect: 'none',
          bgcolor: active ? 'var(--selected-bg)' : 'transparent',
          borderLeft: active ? '2px solid var(--selected-border)' : '2px solid transparent',
          '&:hover': { bgcolor: active ? 'var(--selected-bg)' : 'var(--glass-hover)' },
          transition: 'background-color .12s',
          minHeight: 40,
        }}
      >
        <Box sx={{ ...navIconSx, filter: active ? 'none' : 'grayscale(0.3)' }}>{icon}</Box>
        <Typography sx={{
          ...navLabelSx,
          fontSize: 16, fontWeight: active ? 600 : 400, whiteSpace: 'nowrap',
          color: active ? 'var(--accent-light)' : 'var(--text-secondary)',
        }}>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );

  const SectionLabel = ({ text, color }: { text: string; color?: string }) => collapsed ? null : (
    <Typography sx={{
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
      color: color || 'var(--text-disabled)', px: '11px', pt: '12px', pb: '6px',
    }}>
      {text}
    </Typography>
  );

  const Divider = () => collapsed ? <Box sx={{ my: '6px', borderTop: '1px solid var(--divider)', mx: '8px' }} /> : null;

  return (
    <>
    <Box sx={{
      width: w, flexShrink: 0,
      background: 'var(--sidebar-bg)',
      backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
      borderRight: '1px solid var(--sidebar-border)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      ...(onClose ? { height: '100%' } : {
        minHeight: 'var(--app-h)', position: 'sticky', top: 0, height: 'var(--app-h)',
        transition: 'width .2s ease',
      }),
    }}>
      {/* Logo + toggle */}
      <Box sx={{ px: '12px', borderBottom: '1px solid var(--sidebar-border)', display: 'flex', alignItems: 'center', gap: '10px', height: 52, flexShrink: 0 }}>
        <Box
          onClick={!onClose ? toggle : undefined}
          sx={{ flexShrink: 0, cursor: !onClose ? 'pointer' : 'default', display: 'flex', alignItems: 'center' }}
        >
          <PvLogo size={30} />
        </Box>
        {!collapsed && (
          <>
            <Typography sx={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>
              PromoVideo
            </Typography>
            {!onClose && (
              <Tooltip title="Collapse" placement="right">
                <Box onClick={toggle} sx={{
                  width: 20, height: 20, borderRadius: '6px', border: '1px solid var(--glass-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0, fontSize: 11, color: 'var(--text-disabled)',
                  '&:hover': { bgcolor: 'var(--glass-hover)', color: 'var(--accent-light)' },
                  transition: 'all .15s',
                }}>‹</Box>
              </Tooltip>
            )}
          </>
        )}
      </Box>

      {/* Nav */}
      <Box sx={{ flex: 1, px: railPx, py: '8px', overflowY: 'auto', overflowX: 'hidden' }}>
        {NEW_PLATFORM_ITEMS.map(item => <NavItem key={item.key} icon={item.icon} label={t(item.key)} active={isActive(item.path)} path={item.path} />)}

        {user?.isAdmin && ADMIN_ITEMS.length > 0 && (
          <>
            <Divider />
            {ADMIN_ITEMS.map(item => <NavItem key={item.key} icon={item.icon} label={t(item.key)} active={isActive(item.path)} path={item.path} />)}
          </>
        )}
      </Box>

      {/* Bottom actions — order (top→bottom): Storage | APK | DarkMode | Language | Settings | ── | ChangePwd | UserInfo | Logout */}
      <Box sx={{ borderTop: '1px solid var(--sidebar-border)', flexShrink: 0 }}>

        {/* Storage widget — expanded only, desktop only */}
        {storageStats && !collapsed && !onClose && (() => {
          const pct = storageStats.limit_bytes > 0 ? Math.min(100, (storageStats.used_bytes / storageStats.limit_bytes) * 100) : 0;
          const color = pct > 85 ? '#f87171' : pct > 60 ? '#fbbf24' : 'var(--accent)';
          const usedGB = (storageStats.used_bytes / 1073741824).toFixed(1);
          const totalGB = (storageStats.limit_bytes / 1073741824).toFixed(0);
          return (
            <Box sx={{ px: '10px', pt: '8px', pb: '4px' }}>
              <Box sx={{ px: '10px', py: '8px', borderRadius: '8px', background: 'var(--glass-hover)', border: '1px solid var(--glass-border)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '5px' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Storage</Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{pct.toFixed(0)}%</Typography>
                </Box>
                <Box sx={{ width: '100%', height: '4px', borderRadius: '2px', background: 'var(--glass-border)', overflow: 'hidden', mb: '5px' }}>
                  <Box sx={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '2px', transition: 'width .4s ease' }} />
                </Box>
                <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', fontVariantNumeric: 'tabular-nums' }}>{usedGB} / {totalGB} GB</Typography>
              </Box>
            </Box>
          );
        })()}

        {/* APK download — the player APK is what every operator installs on their
            boxes, so it is not an admin-only tool */}
        <Box sx={{ px: railPx, pt: '8px', pb: '2px' }}>
          <Tooltip title={collapsed ? `Download APK${apkVersion ? ` v${apkVersion}` : ''}` : ''} placement="right">
            <Box
              component="a" href={APK_URL} download
              sx={{
                ...navRowSx, textDecoration: 'none',
                '&:hover': { bgcolor: 'var(--glass-hover)' }, transition: 'background-color .12s',
              }}
            >
              <Box sx={navIconSx}>🤖</Box>
              <Box sx={navLabelSx}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                  Download promovideo.apk
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                  {apkVersion ? `v${apkVersion}` : 'latest'}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        </Box>

        {/* Settings */}
        <Box sx={{ px: railPx, pb: '2px' }}>
          <NavItem icon="⚙" label={t('nav.settings')} active={isActive('/settings')} path="/settings" />
        </Box>

        {/* User card + Logout — mobile drawer only */}
        {onClose && (
          <>
            <Box sx={{ mx: '10px', my: '4px', borderTop: '1px solid var(--divider)' }} />

            {/* User card — tappable, opens profile page */}
            <Box
              onClick={() => { navigate('/profile'); onClose(); }}
              sx={{ px: '20px', py: '8px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', borderRadius: '8px', mx: '10px', mb: '2px', '&:hover': { bgcolor: 'var(--glass-hover)' }, transition: 'background-color .12s' }}
            >
              <Box sx={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), var(--accent-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {user?.login?.[0]?.toUpperCase() || 'A'}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.login || 'user'}</Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mt: '1px' }}>{user?.isAdmin ? t('user.admin') : t('user.operator')}</Typography>
              </Box>
              <Box sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>›</Box>
            </Box>

            <Box sx={{ px: '10px', pb: '10px' }}>
              <Box
                onClick={() => { logout(); onClose(); }}
                sx={{ display: 'flex', alignItems: 'center', gap: '11px', px: '11px', py: '9px', borderRadius: '8px', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(248,113,113,.08)' }, minHeight: 40 }}
              >
                <Box sx={{ fontSize: 18, flexShrink: 0, lineHeight: 1, color: '#f87171' }}>↪</Box>
                <Typography sx={{ fontSize: 16, color: '#f87171', fontWeight: 500 }}>{t('topbar.logout')}</Typography>
              </Box>
            </Box>
          </>
        )}

      </Box>
    </Box>
    </>
  );
};

export default Sidebar;
